import KeyCodes from '@opsbase/lib-general/KeyCodes';
import i18n from '@opsbase/lib-i18n';
import Palette from '@opsbase/lib-palette';
import useCss from '@opsbase/web-css';
import { PlaceIcon, PlusIcon, TickIcon } from '@opsbase/web-icons/allIcons';
import Horizontal from '@opsbase/web-layout/Horizontal';
import Vertical from '@opsbase/web-layout/Vertical';
import TextFieldWithStateAndBlur from '@opsbase/web-textfield/TextFieldWithStateAndBlur';
import Button from '@opsbase/web-ui/Button';
import Dots from '@opsbase/web-ui/Dots';
import Text from '@opsbase/web-ui/Text';
import React, { useCallback, useMemo, useState } from 'react';
import { DataProvider, LayoutProvider, RecyclerListView } from 'recyclerlistview/web';
const makeMyClasses = () => ({
    placeTitle: {
        paddingLeft: 16,
        paddingRight: 16,
        overflow: 'hidden',
        display: '-webkit-box',
        '-webkit-line-clamp': '2',
        '-webkit-box-orient': 'vertical',
        maxWidth: 220,
    },
});
const baseDataProvider = new DataProvider((p1, p2) => p1.updatedDate !== p2.updatedDate);
const PlaceList = (props) => {
    const { places, selectedPlace, onRowPressed, level, total, shown, filtered, resetFilters, loadNextPage, loaded, createNew, } = props;
    const [newPlaceTitle, setNewPlaceTitle] = useState('');
    const [busy, setBusy] = useState(false);
    const layoutProvider = useMemo(() => new LayoutProvider(() => 0, (type, dim) => {
        dim.height = 60;
        dim.width = 300;
    }), []);
    const dataProvider = useMemo(() => baseDataProvider.cloneWithRows(places !== null && places !== void 0 ? places : []), [places]);
    const handleRowClick = useCallback((place) => {
        onRowPressed(place, level);
    }, [level, onRowPressed]);
    const myCss = useCss(makeMyClasses);
    const rowRenderer = useCallback((type, place) => {
        var _a;
        const selected = place.id === selectedPlace;
        const color = place.shouldBeDimmed ? Palette.GreyBlue : Palette.Black;
        return (React.createElement(Horizontal, { centered: true, style: {
                backgroundColor: selected ? Palette.InputCardGrey.main : undefined,
                display: 'flex',
            }, onClick: () => handleRowClick(place) },
            React.createElement(PlaceIcon, { color: color, style: { marginLeft: 16, flex: 0 } }),
            React.createElement(Vertical, { justify: 'center', style: { flex: 1 } },
                React.createElement(Text, { className: myCss.placeTitle, style: { color: color.main }, centered: true }, place.title),
                place.shouldBeDimmed ? (React.createElement(Text, { centered: true, variant: 'tiny', color: 'grey', style: { marginTop: 4 } }, i18n.t('child-places-matching-filter'))) : null),
            React.createElement(Text, { style: { marginRight: 16, flex: 0 } }, `(${(_a = place.noOfChildren) !== null && _a !== void 0 ? _a : '-'})`),
            React.createElement("div", { style: {
                    borderRadius: 4,
                    height: 8,
                    width: 8,
                    backgroundColor: place.enabled
                        ? Palette.Greeny.main
                        : Palette.GreyBlue.main,
                    position: 'absolute',
                    right: 4,
                } })));
    }, [handleRowClick, myCss.placeTitle, selectedPlace]);
    const filteredRow = useMemo(() => {
        if (filtered && !Number.isNaN(filtered)) {
            return (React.createElement(Vertical, { style: {
                    minHeight: 50,
                    padding: '8px 16px 8px 16px',
                    backgroundColor: Palette.NoteYellow.main,
                    flex: 0,
                }, centered: true, shrink: true, onClick: resetFilters },
                React.createElement(Text, null, i18n.t('rows-hidden', { count: filtered })),
                React.createElement(Button, { small: true, background: 'transparent', color: Palette.secondary, label: i18n.t('reset-filters'), onClick: resetFilters })));
        }
        return null;
    }, [filtered, resetFilters]);
    const headerRow = useMemo(() => {
        if (shown) {
            return (React.createElement(Vertical, { style: {
                    padding: '8px 16px 8px 16px',
                    borderBottomStyle: 'solid',
                    borderBottomWidth: 1,
                    borderBottomColor: Palette.InputBorderGrey.main,
                    flex: 0,
                }, centered: true },
                React.createElement(Text, { variant: 'small' }, i18n.t('showing-x-of-y', { x: shown, y: total }))));
        }
        return null;
    }, [shown, total]);
    const handleLoadNextPage = useCallback(() => loadNextPage(level), [level, loadNextPage]);
    const footerRow = useMemo(() => {
        if (loaded < total) {
            return (React.createElement(Vertical, { style: {
                    padding: '8px 16px 8px 16px',
                    borderTopStyle: 'solid',
                    borderTopWidth: 1,
                    borderTopColor: Palette.InputBorderGrey.main,
                    flex: 0,
                }, centered: true, onClick: handleLoadNextPage },
                React.createElement(Button, { small: true, background: 'transparent', color: Palette.secondary, label: i18n.t('load-more'), onClick: handleLoadNextPage })));
        }
        return null;
    }, [handleLoadNextPage, loaded, total]);
    const createManyPlaces = useCallback((placeNames) => {
        setBusy(true);
        Promise.all(placeNames.map(z => createNew(z.trim(), level))).finally(() => {
            setNewPlaceTitle('');
            setBusy(false);
        });
    }, [createNew, level]);
    const confirmCreateNew = useCallback(() => createManyPlaces([newPlaceTitle]), [createManyPlaces, newPlaceTitle]);
    const handleEnter = useCallback(e => {
        if (e.keyCode === KeyCodes.ENTER)
            confirmCreateNew();
    }, [confirmCreateNew]);
    const handlePaste = useCallback(e => {
        let pastedText = e.clipboardData.getData('Text');
        if (pastedText.indexOf('\n') > -1) {
            // if we have unsaved text in the placeholder add it to the beginning of the paste string
            if (newPlaceTitle) {
                pastedText = newPlaceTitle.trim() + '\n' + pastedText;
                setNewPlaceTitle('');
            }
            e.preventDefault();
            const itemsToAdd = pastedText
                .split('\r')
                .join('') // Windows linebreaks
                .split('\n')
                .map(value => value.trim())
                .filter(value => value !== '');
            createManyPlaces(itemsToAdd);
        }
    }, [createManyPlaces, newPlaceTitle]);
    const newPageRowTextfield = useMemo(() => {
        return (React.createElement(Vertical, { style: {
                padding: '8px 16px 8px 16px',
                flex: 0,
            }, centered: true }, busy ? (React.createElement(Dots, { color: Palette.primary })) : (React.createElement(Horizontal, { centered: true },
            React.createElement(PlusIcon, { size: 32 }),
            React.createElement(TextFieldWithStateAndBlur, { white: true, inputProps: {
                    style: {
                        fontSize: 16,
                        lineHeight: '20px',
                        color: Palette.secondary.main,
                    },
                }, fullWidth: true, noMargin: true, value: newPlaceTitle, onBlur: setNewPlaceTitle, placeholder: i18n.t('add-new'), onKeyUp: handleEnter, onPaste: handlePaste }),
            React.createElement(Button, { small: true, disabled: !newPlaceTitle, background: 'transparent', color: Palette.secondary, onClick: confirmCreateNew, icon: React.createElement(TickIcon, { height: 16, width: 16 }) })))));
    }, [busy, confirmCreateNew, handleEnter, handlePaste, newPlaceTitle]);
    return (React.createElement("div", { style: {
            minWidth: 300,
            minHeight: 1,
            display: 'flex',
            flexDirection: 'column',
        } },
        headerRow,
        React.createElement(RecyclerListView, { style: { height: 'auto', minHeight: 1 }, layoutProvider: layoutProvider, dataProvider: dataProvider, rowRenderer: rowRenderer, extendedState: props }),
        newPageRowTextfield,
        filteredRow,
        footerRow));
};
export default PlaceList;
